import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ArrowPrevious, Clock, Eye } from "@untitledui/icons";
import { type AffiliateStatusKey, type PartnerImpersonationView, toPersianDigits } from "@mfa-aff/shared";
import { Button } from "@mfa-aff/ui/components/base/buttons/button";
import { leaveToAdmin, useStopImpersonation } from "../api/impersonation";
import { usePanelStatus } from "../api/panel";

const INACTIVE_FA: Partial<Record<AffiliateStatusKey, string>> = {
    closed: "این حساب بسته شده و خود بازاریاب نمی‌تواند وارد شود.",
    suspended: "این حساب تعلیق شده و خود بازاریاب نمی‌تواند وارد شود.",
};

/** «۲۹:۰۵» */
const clock = (ms: number) => {
    const s = Math.max(0, Math.ceil(ms / 1000));
    return toPersianDigits(`${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`);
};

/**
 * «ورود به پنل بازاریاب»: a sticky bar above the whole panel while an admin views it, read-only.
 * Its height becomes `--app-top-offset`, so the fixed sidebar and the sticky headers of
 * DashboardShell sit below it. At the end of the 30 minutes (or on the button) the tab goes back to
 * the partner's page in the admin panel.
 */
export const ImpersonationBar = ({ view, partnerName, status }: { view: PartnerImpersonationView; partnerName: string; status: AffiliateStatusKey }) => {
    const ref = useRef<HTMLDivElement>(null);
    const stop = useStopImpersonation();
    const panel = usePanelStatus().data;
    const expiresAt = new Date(view.expiresAt).getTime();
    const [left, setLeft] = useState(() => expiresAt - Date.now());

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const root = document.documentElement;
        const apply = () => root.style.setProperty("--app-top-offset", `${el.offsetHeight}px`);
        apply();
        const ro = new ResizeObserver(apply);
        ro.observe(el);
        return () => {
            ro.disconnect();
            root.style.removeProperty("--app-top-offset");
        };
    }, []);

    useEffect(() => {
        const id = window.setInterval(() => setLeft(expiresAt - Date.now()), 1000);
        return () => window.clearInterval(id);
    }, [expiresAt]);

    // Time is up: the API no longer accepts this view; go back instead of showing errors.
    useEffect(() => {
        if (left <= 0) leaveToAdmin(view.adminUrl);
    }, [left, view.adminUrl]);

    const back = () => stop.mutate(undefined, { onSettled: (res) => leaveToAdmin(res?.adminUrl, view.adminUrl) });
    const notes = [INACTIVE_FA[status], panel && !panel.open ? "پنل فعلاً برای بازاریاب‌ها بسته است." : null].filter(Boolean);

    return (
        <div
            ref={ref}
            role="region"
            aria-label="مشاهدهٔ پنل توسط مدیر"
            className="sticky top-0 z-[60] border-b border-yellow-500 bg-yellow-300 text-gray-950 shadow-sm"
        >
            <div className="mx-auto flex max-w-screen-2xl flex-col gap-2 px-4 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4 lg:px-6">
                <div className="flex min-w-0 items-start gap-2.5">
                    <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-gray-950 text-yellow-300" aria-hidden="true">
                        <Eye className="size-4" />
                    </span>
                    <div className="min-w-0">
                        <p className="text-sm font-semibold leading-6">
                            مشاهدهٔ پنل <bdi>{partnerName}</bdi> توسط مدیر
                            <span className="mx-1.5 font-normal" aria-hidden="true">·</span>
                            <span className="whitespace-nowrap">فقط مشاهده</span>
                        </p>
                        {notes.length > 0 && <p className="text-xs leading-5 text-gray-800">{notes.join(" ")}</p>}
                    </div>
                </div>
                <div className="flex shrink-0 items-center justify-between gap-3 sm:justify-end">
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium tabular-nums" title="زمان باقی‌مانده از ۳۰ دقیقه">
                        <Clock className="size-4" aria-hidden="true" />
                        <span>{clock(left)} مانده</span>
                    </span>
                    <Button size="sm" color="secondary" iconLeading={ArrowPrevious} onPress={back} isLoading={stop.isPending} showTextWhileLoading>
                        برگشت به پنل مدیریت
                    </Button>
                </div>
            </div>
        </div>
    );
};
